import { Stack, Typography, IconButton } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { DestinationIcon, RouteStartIcon, TurnLeftIcon, TurnRightIcon, ElevatorIcon, StairsIcon, StraightArrowIcon } from '@/components/navigation/icons/Icons';
import { trimDescription } from '@/components/navigation/search-bar/util';
import { navigationTranslationState } from '../../store';

type DetailedRoutesProps = {
  currentSweep?: any;
  detailedRoutes?: any[];
  openDetailedRoutes: () => void;
  isBackgroundVisible: boolean;
  rtl?: boolean;
};

const directionIconsMap = {
  left: <TurnLeftIcon color="white" />,
  right: <TurnRightIcon color="white" />,
};

const teleportIconsMap = {
  elevator: <ElevatorIcon color="white" />,
  stairway: <StairsIcon color="white" />,
};

const iconWrapperStyle = {
  width: '36.406px',
  height: '36.406px',
  padding: '5px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '60px',
  background: ' rgba(255, 255, 255, 0.10)',
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
};

export const DetailedRoutesMobile = ({ currentSweep, detailedRoutes, openDetailedRoutes, isBackgroundVisible, rtl }: DetailedRoutesProps) => {
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [waitingItem, setWaitingItem] = useState(null);
  const translation = useRecoilValue(navigationTranslationState);

  const renderRouteDetails = useCallback((route, index) => {
    if (index === 0) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>
            <RouteStartIcon color="white" />
          </Stack>
          <Typography
            fontSize="16px"
            fontWeight="400"
            textAlign="start"
            fontFamily="Inter"
            whiteSpace="break-spaces"
            textOverflow="ellipsis"
            sx={{ wordBreak: 'break-word', direction: rtl ? 'rtl' : '' }}
          >
            <>
              {translation?.navStartAt || 'Start at'} <strong>{trimDescription(route?.tag?.title || route?.station?.title || route?.title || route?.name, 50)}</strong>
            </>
          </Typography>
        </>
      );
    }
    if (route.turn) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>{directionIconsMap[route.turn]}</Stack>
          <Typography fontSize="16px" fontWeight="400" textAlign="start" sx={{ direction: rtl ? 'rtl' : '' }}>
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              <strong>
                {translation?.navThen || 'Then'} {translation?.navTurn || 'turn'}{' '}
                {route.turn === 'left' ? `${translation?.navLeft || route.turn}` : `${translation?.navRight || route.turn}`}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (route.tpGroupStart) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>{teleportIconsMap[route.tpGroupStart.type]}</Stack>
          <Typography fontSize="16px" fontWeight="400" textAlign="start" sx={{ direction: rtl ? 'rtl' : '' }}>
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              {translation?.navThen || 'Then'}{' '}
              <strong>
                {route.tpGroupStart.type === 'elevator' ? translation?.navEnterTheElevator || 'enter the elevator' : translation?.navTakeTheStairs || 'take the stairs'}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (route.tpGroupEnd) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>{teleportIconsMap[route.tpGroupEnd.type]}</Stack>
          <Typography fontSize="16px" fontWeight="400" textAlign="start" sx={{ direction: rtl ? 'rtl' : '' }}>
            <>
              {translation?.navSelect || 'Select'} {route.tpGroupEnd.name} <br />
              {translation?.navUponReachingTheFloor || 'Upon reaching the floor'},{' '}
              <strong>
                {route.tpGroupEnd.turn
                  ? `${translation?.navTurn || 'turn'} ${
                      route.tpGroupEnd.turn === 'left' ? translation?.navLeft || route.tpGroupEnd.turn : translation?.navRight || route.tpGroupEnd.turn
                    }`
                  : `${translation?.navProceedStraight || 'proceed straight'}`}
              </strong>
            </>
          </Typography>
        </>
      );
    }
    if (route.isMandatory) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>
            <StraightArrowIcon color="white" />
          </Stack>
          <Typography fontSize="16px" fontWeight="400" textAlign="start" sx={{ direction: rtl ? 'rtl' : '' }}>
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              <strong>
                {translation?.navThen || 'Then'} {translation?.navThenProceedStraightAt || 'proceed straight at'} {route?.title || route?.name}
              </strong>
            </>
          </Typography>
        </>
      );
    }

    if (route.isFinish) {
      return (
        <>
          <Stack sx={iconWrapperStyle}>
            <DestinationIcon color="white" />
          </Stack>
          <Typography fontSize="16px" fontWeight="400" textAlign="start" sx={{ direction: rtl ? 'rtl' : '' }}>
            <>
              {translation?.navWalk || 'Walk'} {route.distance}m <br />
              <strong>translation?.navYouHaveReached || 'You have reached' {route?.title}</strong>
            </>
          </Typography>
        </>
      );
    }
    return null;
  }, []);

  useEffect(() => {
    const foundItem = detailedRoutes.find((r) => r.id === currentSweep?.id);

    if (!foundItem && !highlightedItem && !waitingItem) {
      setHighlightedItem({ ...detailedRoutes[0], routeIndex: 0 });
      return;
    }
    if (foundItem) {
      const foundItemIndex = detailedRoutes.findIndex((r) => r.id === foundItem.id);
      const nextWaitingItem = detailedRoutes[foundItemIndex + 1];

      setHighlightedItem({ ...foundItem, routeIndex: foundItemIndex });
      setWaitingItem(nextWaitingItem);
    } else {
      const highlightedItemIndex = detailedRoutes.findIndex((r) => r.id === highlightedItem?.id);
      const nextItem = detailedRoutes[highlightedItemIndex + 1];
      // Only update if the next item is not already highlighted
      if (nextItem && waitingItem && nextItem.id === waitingItem.id) {
        setHighlightedItem(nextItem);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSweep?.id, detailedRoutes?.length, waitingItem]);

  return (
    !isBackgroundVisible && (
      <Stack
        id="detailed-routes-mobile"
        sx={{
          p: '10.611px 9px',
          alignItems: 'center',
          position: 'absolute',
          width: 'calc(100vw - 20%)',
          left: '10%',
          right: '10%',
          top: '50px',
          borderRadius: '28.651px',
          background: 'rgba(0, 0, 0, 0.60)',
          backdropFilter: 'blur(7.5px)',
          color: '#fff',
        }}
      >
        {highlightedItem && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            sx={{
              width: '100%',
              gap: '10px',
            }}
          >
            {renderRouteDetails(highlightedItem, highlightedItem.routeIndex)}
            <IconButton onClick={openDetailedRoutes} aria-label="expand" size="small" sx={{ marginLeft: 'auto' }}>
              <i className="fa-solid fa-chevron-down" style={{ color: 'white' }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
    )
  );
};
